import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { User, Lock, Shield, Check, PlusCircle, Clock, Globe } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";

export default function Profile() {
  const { active } = useTournament();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [reqName, setReqName] = useState("");
  const [reqDesc, setReqDesc] = useState("");
  const [reqVisibility, setReqVisibility] = useState("public");

  useEffect(() => {
    if (!active || !user) return;
    (async () => {
      const tid = active.tournament_id;
      const [pR, gR, tR, rR] = await Promise.all([
        api.get(`/profile/me`, { params: { tournament_id: tid } }),
        api.get(`/tournaments/${tid}/groups`),
        api.get(`/tournaments/${tid}/teams`),
        api.get(`/tournaments/${tid}/group-requests/mine`),
      ]);
      setProfile(pR.data);
      setGroups(gR.data);
      setTeams(tR.data);
      setMyRequests(rR.data);
    })();
  }, [active, user]);

  const submitRequest = async () => {
    if (!reqName.trim()) { toast.error("Enter a group name"); return; }
    setBusy(true);
    try {
      await api.post(`/tournaments/${active.tournament_id}/group-requests`, { name: reqName.trim(), description: reqDesc.trim(), visibility: reqVisibility });
      toast.success("Request submitted — an admin will review it");
      setReqName(""); setReqDesc(""); setReqVisibility("public");
      const { data } = await api.get(`/tournaments/${active.tournament_id}/group-requests/mine`);
      setMyRequests(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request");
    } finally { setBusy(false); }
  };

  const update = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/profile/me`, { ...patch, tournament_id: active.tournament_id });
      setProfile(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  if (!active || !profile) return null;
  const locked = active.config?.affiliations_locked;
  const myGroups = profile.group_ids || [];

  const toggleGroup = (gid) => {
    const next = myGroups.includes(gid) ? myGroups.filter((x) => x !== gid) : [...myGroups, gid];
    update({ group_ids: next });
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)] max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <User size={20} className="text-primary-accent"/>
        <div>
          <div className="label-tag">Your Profile</div>
          <h1 className="font-display text-3xl md:text-5xl uppercase">{profile.display_name || user.name}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="surface-card p-5 accent-bar">
          <h2 className="font-display text-xl uppercase mb-3">Identity</h2>
          <label className="label-tag block mb-1">Display Name</label>
          <input
            data-testid="display-name-input" type="text"
            value={profile.display_name || ""}
            onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 mb-3 focus:border-primary-accent focus:outline-none"
          />
          <label className="label-tag block mb-1">Profile Photo URL</label>
          <input
            data-testid="picture-input" type="text" placeholder="https://..."
            value={profile.picture || ""}
            onChange={(e) => setProfile({ ...profile, picture: e.target.value })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 mb-3 focus:border-primary-accent focus:outline-none"
          />
          <button
            data-testid="save-identity"
            onClick={() => update({ display_name: profile.display_name, picture: profile.picture })}
            disabled={busy}
            className="bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest"
          >Save Identity</button>
        </div>

        <div className="surface-card p-5">
          <h2 className="font-display text-xl uppercase mb-3">Favourite Team</h2>
          <select
            data-testid="favourite-team-select"
            value={profile.favourite_team || ""}
            onChange={(e) => update({ favourite_team: e.target.value || null })}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
          >
            <option value="">No favourite</option>
            {teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.code} · {t.name}</option>)}
          </select>
          {profile.favourite_team && (
            <div className="mt-3"><TeamBadge team={teams.find(t => t.team_id === profile.favourite_team)} size="lg"/></div>
          )}
        </div>
      </div>

      <div className="surface-card p-5 mt-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-primary-accent"/>
            <h2 className="font-display text-xl uppercase">Group Affiliations</h2>
          </div>
          {locked && (
            <span className="flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent" data-testid="affiliation-locked">
              <Lock size={12}/> Locked
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          You can represent <span className="text-white">multiple groups</span>.
          Your individual points contribute to each one&apos;s standing. {locked ? "Affiliations are locked for this tournament." : "You can add/remove anytime until the tournament begins."}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {groups.map((g) => {
            const selected = myGroups.includes(g.group_id);
            const inviteOnly = (g.visibility || "public") === "invite_only";
            const joinable = !inviteOnly || selected; // invite-only: only members may toggle (to leave)
            const disabled = locked || busy || !joinable;
            return (
              <button
                key={g.group_id}
                data-testid={`group-${g.group_id}`}
                onClick={() => !disabled && toggleGroup(g.group_id)}
                disabled={disabled}
                className={`p-3 border text-left transition-colors relative ${
                  selected ? "border-primary-accent bg-white/5" : "border-zinc-800 hover:border-white/30"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                style={{ borderLeftWidth: 4, borderLeftColor: g.color }}
              >
                {selected && <Check size={14} className="absolute top-2 right-2 text-primary-accent"/>}
                <div className="font-display text-base">{g.name}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-0.5">{g.description || "—"}</div>
                <span className={`inline-flex items-center gap-1 mt-1.5 text-[10px] uppercase tracking-widest ${inviteOnly ? "text-zinc-400" : "text-green-400"}`} data-testid={`group-visibility-${g.group_id}`}>
                  {inviteOnly ? <><Lock size={10}/> Invite only</> : <><Globe size={10}/> Public</>}
                </span>
              </button>
            );
          })}
        </div>

        {myGroups.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="label-tag self-center">Representing:</span>
            {myGroups.map((gid) => {
              const g = groups.find((x) => x.group_id === gid);
              if (!g) return null;
              return (
                <span key={gid} className="inline-flex items-center gap-2 px-2 py-1 border border-zinc-800 text-xs uppercase tracking-widest" style={{ borderLeftWidth: 3, borderLeftColor: g.color }}>
                  {g.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* REQUEST A NEW GROUP */}
      <div className="surface-card p-5 mt-6" data-testid="request-group-card">
        <div className="flex items-center gap-3 mb-3">
          <PlusCircle size={18} className="text-primary-accent" />
          <h2 className="font-display text-xl uppercase">Request a New Group</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Want a group that doesn&apos;t exist yet? Request it — an admin will review it, and you&apos;ll become its admin once approved.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <input
            data-testid="request-group-name" type="text" placeholder="Group name"
            value={reqName} onChange={(e) => setReqName(e.target.value)} maxLength={60}
            className="bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
          />
          <input
            data-testid="request-group-desc" type="text" placeholder="Description (optional)"
            value={reqDesc} onChange={(e) => setReqDesc(e.target.value)} maxLength={160}
            className="bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none"
          />
          <select
            data-testid="request-group-visibility" value={reqVisibility}
            onChange={(e) => setReqVisibility(e.target.value)}
            className="bg-zinc-950 border border-zinc-800 px-3 py-2 focus:border-primary-accent focus:outline-none text-sm"
          >
            <option value="public">Public — anyone can join</option>
            <option value="invite_only">Invite-only — via link</option>
          </select>
        </div>
        <button
          data-testid="request-group-submit" onClick={submitRequest} disabled={busy}
          className="bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-50"
        >Submit Request</button>

        {myRequests.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="my-requests-list">
            <div className="label-tag mb-1">Your requests</div>
            {myRequests.map((r) => (
              <div key={r.request_id} data-testid={`my-request-${r.request_id}`} className="flex items-center justify-between border border-white/5 px-3 py-2">
                <div className="min-w-0">
                  <div className="font-display text-sm uppercase truncate">{r.name}</div>
                  {r.description && <div className="text-xs text-zinc-500 truncate">{r.description}</div>}
                </div>
                <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest flex-shrink-0 ml-3 ${
                  r.status === "approved" ? "text-green-400" : r.status === "rejected" ? "text-zinc-500" : "text-yellow-400"
                }`}>
                  {r.status === "pending" && <Clock size={12} />}
                  {r.status === "approved" && <Check size={12} />}
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
